import { useState } from 'react';
import { Settings, Building2, Mail, Database, Plug, Key } from '../icons/wp-icons';
import { Navigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import PlanWidget from '../components/Settings/PlanWidget';
import GoogleCalendarSettings from '../components/Settings/GoogleCalendarSettings';
import OutlookCalendarSettings from '../components/Settings/OutlookCalendarSettings';
import EasybellSettings from '../components/Settings/EasybellSettings';
import EmailSettings from '../components/Settings/EmailSettings';
import MahnwesenSettings from '../components/Settings/MahnwesenSettings';
import BuchhaltungSettings from '../components/Settings/BuchhaltungSettings';
import DatenImportSettings from '../components/Settings/DatenImportSettings';
import PushNotificationSettings from '../components/Settings/PushNotificationSettings';
import FirmendatenSettings from '../components/Settings/FirmendatenSettings';
import DesignSettings from '../components/Settings/DesignSettings';
import OnboardingResetSettings from '../components/Settings/OnboardingResetSettings';
import DangerZoneSettings from '../components/Settings/DangerZoneSettings';
import ProaktiveAktionenSettings from '../components/Settings/ProaktiveAktionenSettings';
import PdsIntegrationSettings from '../components/Settings/PdsIntegrationSettings';
import HeroIntegrationSettings from '../components/Settings/HeroIntegrationSettings';
import TaifunIntegrationSettings from '../components/Settings/TaifunIntegrationSettings';
import StreitIntegrationSettings from '../components/Settings/StreitIntegrationSettings';
import LexwareIntegrationSettings from '../components/Settings/LexwareIntegrationSettings';
import CalendlySettings from '../components/Settings/CalendlySettings';
import GoogleReviewSettings from '../components/Settings/GoogleReviewSettings';
import ApiKeySettings from '../components/Settings/ApiKeySettings';
import { usePermissions, MODULES } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';

// Tab-Konfiguration (KI-Tab entfernt - jetzt unter /ki-zentrale)
const ALL_TABS = [
  { id: 'allgemein', label: 'Allgemein', icon: Building2 },
  { id: 'kommunikation', label: 'Kommunikation', icon: Mail },
  { id: 'buchhaltung', label: 'Buchhaltung & Daten', icon: Database },
  { id: 'integrationen', label: 'Integrationen', icon: Plug },
  { id: 'api-keys', label: 'API-Schlüssel', icon: Key }
];

// HEYKIKI-Orgs sehen "Allgemein", "Kommunikation" und "Integrationen" (aktuell
// nur PDS). Buchhaltung & API-Schlüssel sind für den Sub-Kunden nicht relevant.
const HEYKIKI_TABS = ['allgemein', 'kommunikation', 'integrationen'];

function Einstellungen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'allgemein';
  const [activeTab, setActiveTab] = useState(initialTab === 'ki' ? 'allgemein' : initialTab);

  const { canView, canEdit, loading } = usePermissions();
  const { user } = useAuth();
  const readOnly = !canEdit(MODULES.EINSTELLUNGEN);
  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';
  const TABS = isHeykikiOrg
    ? ALL_TABS.filter(t => HEYKIKI_TABS.includes(t.id))
    : ALL_TABS;

  // Fallback wenn URL-Param einen Tab zeigt, der für HEYKIKI nicht existiert
  // (z.B. Deep-Link auf ?tab=integrationen).
  if (isHeykikiOrg && !HEYKIKI_TABS.includes(activeTab) && activeTab !== 'ki') {
    return <Navigate to="/einstellungen?tab=allgemein" replace />;
  }

  // Redirect old KI tab to KI Zentrale
  if (initialTab === 'ki') {
    return <Navigate to="/ki-zentrale" replace />;
  }

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  // Warten bis Berechtigungen geladen sind
  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </Layout>
    );
  }

  // Prüfen ob Benutzer Einstellungen sehen darf
  if (!canView(MODULES.EINSTELLUNGEN)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Settings className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
          <h1 className="text-2xl sm:text-3xl font-serif font-bold">Einstellungen</h1>
        </div>

        {/* Tabs */}
        <div className="bg-surface rounded-card shadow-card mb-6 overflow-hidden">
          <nav className="flex overflow-x-auto scrollbar-hide border-b border-border">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-4 sm:space-y-6">
          {/* Tab: Allgemein */}
          {activeTab === 'allgemein' && (
            <>
              {!isHeykikiOrg && <PlanWidget />}
              <FirmendatenSettings readOnly={readOnly} />
              <DesignSettings readOnly={readOnly} />
              <PushNotificationSettings readOnly={readOnly} />
              <ProaktiveAktionenSettings readOnly={readOnly} />
              <OnboardingResetSettings readOnly={readOnly} />
              <DangerZoneSettings />
            </>
          )}

          {/* Tab: Kommunikation */}
          {activeTab === 'kommunikation' && (
            <>
              <EmailSettings readOnly={readOnly} />
              <GoogleCalendarSettings readOnly={readOnly} />
              <OutlookCalendarSettings readOnly={readOnly} />
              {!isHeykikiOrg && <EasybellSettings readOnly={readOnly} />}
              <CalendlySettings readOnly={readOnly} />
              <GoogleReviewSettings readOnly={readOnly} />
            </>
          )}

          {/* Tab: Buchhaltung & Daten */}
          {activeTab === 'buchhaltung' && (
            <>
              <MahnwesenSettings readOnly={readOnly} />
              <BuchhaltungSettings readOnly={readOnly} />
              <DatenImportSettings readOnly={readOnly} />
            </>
          )}

          {/* Tab: Integrationen */}
          {activeTab === 'integrationen' && isHeykikiOrg && (
            <PdsIntegrationSettings readOnly={readOnly} />
          )}
          {activeTab === 'integrationen' && !isHeykikiOrg && (
            <>
              <PdsIntegrationSettings readOnly={readOnly} />
              <HeroIntegrationSettings readOnly={readOnly} />
              <TaifunIntegrationSettings readOnly={readOnly} />
              <StreitIntegrationSettings readOnly={readOnly} />
              <LexwareIntegrationSettings readOnly={readOnly} />
            </>
          )}

          {/* Tab: API-Schlüssel */}
          {activeTab === 'api-keys' && (
            <ApiKeySettings readOnly={readOnly} />
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Einstellungen;
